import { BitmapLayer } from "@deck.gl/layers";
import type { ShaderModule } from "@luma.gl/shadertools";
import { useCallback } from "react";

export type FilterMode = "nearest" | "linear" | "lanczos";

export type ImageAdjustProps = {
    contrast:       number;     //   -1 to 1, default 0
    gamma:          number;     //  0.1 to 5, default 1
    saturation:     number;     //   -1 to 1, default 0
    filterMode:     FilterMode;
    halfResolution: boolean;
    weight:         number;
};

export const IMAGE_ADJUST_DEFAULTS: ImageAdjustProps = {
    contrast:       0,
    gamma:          1,
    saturation:     0,
    filterMode:     "lanczos",
    halfResolution: false,
    weight:         3.0,
};

type ImageAdjustUniforms = {
    contrast:       number;
    gamma:          number;
    saturation:     number;
    filterMode:     number;
    texSize:        [number, number];
    halfResolution: number;
    weight:         number;
};

const uniformBlock = `\
uniform imageAdjustUniforms {
    float contrast;
    float gamma;
    float saturation;
    float filterMode;
    vec2  texSize;
    float halfResolution;
    float weight;
} imageAdjust;
`;

// Forward-declare the sampler that BitmapLayer defines in its main shader source.
// GLSL allows redundant uniform declarations as long as the types match exactly.
// This lets our helper function reference it, since module code is injected
// BEFORE the main shader source.
const samplerForwardDecl = `
uniform sampler2D bitmapTexture;
`;

const lanczosWeightFn = `
float lanczosWeight( float w, float y ) {
    float LANCZOS_A     = w;
    int   LANCZOS_START = -int( LANCZOS_A ) + 1;
    int   LANCZOS_END   =  int( LANCZOS_A );
    if( y < 1e-6       ) { return 1.0; }
    if( y >= LANCZOS_A ) { return 0.0; }
    float py = 3.14159265 * y;
    return LANCZOS_A * sin( py ) * sin( py / LANCZOS_A ) / ( py * py );
}
vec4 _halfResAvg( ivec2 base ) {
    vec4 a = texelFetch( bitmapTexture, base,                 0 );
    vec4 b = texelFetch( bitmapTexture, base + ivec2( 1, 0 ), 0 );
    vec4 c = texelFetch( bitmapTexture, base + ivec2( 0, 1 ), 0 );
    vec4 d = texelFetch( bitmapTexture, base + ivec2( 1, 1 ), 0 );
    return ( a + b + c + d ) * 0.25;
}

// Fetch from the "virtual" source texture. When resStep == 2, half-res is on
// and we return a 2x2 box average of the underlying real texels. When resStep
// == 1, we return the texel directly. Coord is in virtual-texel units.
vec4 _vFetch( ivec2 coord, int resStep ) {
    if( resStep == 2 ) { return _halfResAvg( coord * 2 ); }
    return texelFetch( bitmapTexture, coord, 0 );
}`;

const imageAdjustModule = {
    name: "imageAdjust",
    // Only uniforms + pure math functions — NO sampler references here.
    fs: uniformBlock + samplerForwardDecl + lanczosWeightFn,
    uniformTypes: {
        contrast:       "f32",
        gamma:          "f32",
        saturation:     "f32",
        filterMode:     "f32",
        texSize:        "vec2<f32>",
        halfResolution: "f32",
        weight:         "f32"
    },
    defaultUniforms: {
        contrast:             0,
        gamma:                1,
        saturation:           0,
        filterMode:           0,
        texSize:         [1, 1],
        halfResolution:       0,
        weight:             3.0,
    },
    inject: {
        // At this injection point (line ~300 in compiled shader),
        // `bitmapTexture` (sampler2D) and `geometry.uv` are both in scope.
        // We inline the full Lanczos sampling here.
        "fs:DECKGL_FILTER_COLOR": `
            int   _hrStep   = imageAdjust.halfResolution > 0.5 ? 2 : 1;
            vec2  _vTexSize = imageAdjust.texSize / float( _hrStep );

            if( imageAdjust.filterMode > 0.5 ) {
                //const float _subdiv = 8.0;  // 2 = double res, 3 = triple, etc.

                // Snap to subdivided grid: one filter evaluation per subdiv cell.
                //vec2  _grid    = _vTexSize * _subdiv;
                //vec2  _lzPixel = ( floor( geometry.uv * _grid ) + 0.5 ) / _grid * _vTexSize;

                //this is 
                vec2 _lzPixel = geometry.uv * _vTexSize;
                vec2  _lzCenter = floor( _lzPixel - 0.5 ) + 0.5;
                ivec2 _lzBase   = ivec2( _lzCenter );

                if( imageAdjust.filterMode > 1.5 ) {
                    // Lanczos-4: 8x8 neighbourhood on the virtual texture.
                    vec3  _lzCol  = vec3( 0.0 );
                    float _lzWSum = 0.0;

                    float LANCZOS_A     = imageAdjust.weight;
                    int   LANCZOS_START = -int( LANCZOS_A ) + 1;
                    int   LANCZOS_END   =  int( LANCZOS_A );
                    for( int _lzJ = LANCZOS_START; _lzJ <= LANCZOS_END; _lzJ++ ) {
                        for( int _lzI = LANCZOS_START; _lzI <= LANCZOS_END; _lzI++ ) {
                            vec2  _lzOff   = vec2( float( _lzI ), float( _lzJ ) );
                            vec2  _lzD     = abs( _lzPixel - ( _lzCenter + _lzOff ) );
                            float _lzW     = lanczosWeight( LANCZOS_A, _lzD.x ) * lanczosWeight( LANCZOS_A, _lzD.y );
                            ivec2 _lzCoord = _lzBase + ivec2( _lzI, _lzJ );
                            _lzCol        += _vFetch( _lzCoord, _hrStep ).rgb * _lzW;
                            _lzWSum       += _lzW;
                        }
                    }
                    _lzCol /= _lzWSum;

                    // Anti-ringing clamp against the 4 center texels of the kernel.
                    vec3 _lzC11 = _vFetch( _lzBase,                 _hrStep ).rgb;
                    vec3 _lzC21 = _vFetch( _lzBase + ivec2( 1, 0 ), _hrStep ).rgb;
                    vec3 _lzC12 = _vFetch( _lzBase + ivec2( 0, 1 ), _hrStep ).rgb;
                    vec3 _lzC22 = _vFetch( _lzBase + ivec2( 1, 1 ), _hrStep ).rgb;
                    vec3 _lzLo  = min( min( _lzC11, _lzC21 ), min( _lzC12, _lzC22 ) );
                    vec3 _lzHi  = max( max( _lzC11, _lzC21 ), max( _lzC12, _lzC22 ) );
                    _lzCol = mix( _lzCol, clamp( _lzCol, _lzLo, _lzHi ), 0.5 );

                    color = vec4( _lzCol, _vFetch( _lzBase, _hrStep ).a );
                } else {
                    // Bilinear: 2x2 on the virtual texture.
                    vec2 _lzF  = _lzPixel - _lzCenter;
                    vec4 _lzTL = _vFetch( _lzBase,                 _hrStep );
                    vec4 _lzTR = _vFetch( _lzBase + ivec2( 1, 0 ), _hrStep );
                    vec4 _lzBL = _vFetch( _lzBase + ivec2( 0, 1 ), _hrStep );
                    vec4 _lzBR = _vFetch( _lzBase + ivec2( 1, 1 ), _hrStep );
                    color = mix( mix( _lzTL, _lzTR, _lzF.x ), mix( _lzBL, _lzBR, _lzF.x ), _lzF.y );
                }
            } else if ( imageAdjust.halfResolution > 0.5 ) {
                // No filtering, but half-res on: nearest-neighbor on the virtual texture.
                color = _vFetch( ivec2( geometry.uv * _vTexSize ), _hrStep );
            }
            color.rgb = ( color.rgb - 0.5 ) * ( 1.0 + imageAdjust.contrast ) + 0.5;                           // contrast
            color.rgb = pow( clamp( color.rgb, vec3( 0.0 ), vec3( 1.0 ) ), vec3( 1.0 / imageAdjust.gamma ) ); // gamma
            float lum = dot( color.rgb, vec3( 0.2126, 0.7152, 0.0722 ) );                                     // saturation
            color.rgb = mix( vec3( lum ), color.rgb, 1.0 + imageAdjust.saturation );
        `
    },
} as const satisfies ShaderModule<ImageAdjustProps, ImageAdjustUniforms>;

//subclass BitmapLayer
export interface AdjustableBitmapLayerProps {
    imageAdjust?: ImageAdjustProps;
    imageWidth:   number;
    imageHeight:  number;
}

export class AdjustableBitmapLayer extends BitmapLayer<AdjustableBitmapLayerProps> {
    static layerName = "AdjustableBitmapLayer";
    static defaultProps = {
        ...BitmapLayer.defaultProps,
        imageAdjust:  { type: "object", value: IMAGE_ADJUST_DEFAULTS },
        imageWidth:   { type: "number", value: 1 },
        imageHeight:  { type: "number", value: 1 },
    };

    getShaders() {
        const shaders = super.getShaders();
        // The base BitmapLayer declares `uniform sampler2D bitmapTexture;` in its
        // fragment shader source. Our module also declares it (earlier) so that
        // the Lanczos helper can reference it. GLSL doesn't allow redundant
        // uniform declarations on all drivers, so we strip the one from the
        // main shader source to avoid "redefinition" errors.
        const fs = shaders.fs.replace( /uniform\s+sampler2D\s+bitmapTexture\s*;/g, "// bitmapTexture declared by imageAdjust module" );
        return {
            ...shaders,
            fs,
            modules: [...shaders.modules, imageAdjustModule],
        };
    }

    draw( opts: any ) {
        const props = this.props;
        const adjust: ImageAdjustProps = props.imageAdjust ?? IMAGE_ADJUST_DEFAULTS;
        const filterModeValue = adjust.filterMode === "lanczos" ? 2 : adjust.filterMode === "linear" ? 1 : 0;
        this.state.model?.shaderInputs.setProps( {
            imageAdjust: {
                contrast:   adjust.contrast,
                gamma:      adjust.gamma,
                saturation: adjust.saturation,
                filterMode: filterModeValue,
                halfResolution: adjust.halfResolution ? 1 : 0,
                texSize:    [props.imageWidth, props.imageHeight],
                weight:     adjust.weight
            },
        } );
        super.draw( opts );
    }
}

//ui panel
interface Props {
    value: ImageAdjustProps;
    onChange: ( v: ImageAdjustProps ) => void;
}

const FILTER_MODES: { value: FilterMode; label: string }[] = [
    { value: "nearest", label: "Nearest" },
    { value: "linear",  label: "Linear"  },
    { value: "lanczos", label: "Lanczos" },
];

export function ImageAdjustPanel( { value, onChange }: Props ) {
    const set = useCallback(
        ( key: keyof ImageAdjustProps, v: number ) => onChange( { ...value, [key]: v } ),
        [value, onChange]
    );

    const sliders: { key: keyof ImageAdjustProps; label: string; min: number; max: number; step: number }[] = [
        { key: "contrast",    label: "Contrast",       min: -1.0, max: 1, step: 0.01 },
        { key: "gamma",       label: "Gamma",          min:  0.1, max: 5, step: 0.01 },
        { key: "saturation",  label: "Saturation",     min: -1.0, max: 1, step: 0.01 },
     // { key: "weight",      label: "Lanczos Weight", min: 1,    max: 4, step: 0.1  },
    ];

    return (
        <div
            style={{
                position: "absolute",
                bottom: 16,
                right: 16,
                zIndex: 10,
                background: "rgba(0,0,0,0.75)",
                color: "#fff",
                borderRadius: 8,
                padding: "12px 16px",
                minWidth: 220,
                fontSize: 13,
            }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>Settings</strong>
                <button
                    className="btn btn-sm btn-outline-light py-0 px-1"
                    style={{ fontSize: 11 }}
                    onClick={() => onChange( IMAGE_ADJUST_DEFAULTS )}>
                    Reset
                </button>
            </div>
            <div className="mb-2">
                <div className="btn-group btn-group-sm w-100">
                    {FILTER_MODES.map( fm => (
                        <button
                            key={fm.value}
                            className={`btn ${value.filterMode === fm.value ? "btn-light" : "btn-outline-light"}`}
                            style={{ fontSize: 11 }}
                            onClick={() => onChange( { ...value, filterMode: fm.value } )}>
                            {fm.label}
                        </button>
                    ) )}
                </div>
            </div>
            {/* <div className="mb-2 form-check">
                <input
                    type="checkbox"
                    className="form-check-input"
                    id="halfResCheck"
                    checked={value.halfResolution}
                    onChange={e => onChange( { ...value, halfResolution: e.target.checked } )}
                />
                <label className="form-check-label" htmlFor="halfResCheck">
                    Half resolution
                </label>
            </div> */}
            {sliders.map( ( { key, label, min, max, step } ) => (
                <div key={key} className="mb-1">
                    <div className="d-flex justify-content-between">
                        <span>{label}</span>
                        <span style={{ opacity: 0.6 }}>{( value[key] as number ).toFixed(2)}</span>
                    </div>
                    <input
                        type="range"
                        className="w-100"
                        min={min}
                        max={max}
                        step={step}
                        value={value[key] as number}
                        onChange={e => set( key, parseFloat( e.target.value ) )}
                    />
                </div>
            ) )}
        </div>
    );
}